import { useState } from 'react';
import { ReturnButton } from '@/components/shared/ReturnButton';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/utils/styles';
import { L4Card } from '@screens/HomePage/MoodMemoryAndFocusPage/L4Card';
import { BadgeSection } from '@screens/HomePage/MoodMemoryAndFocusPage/BadgeSection';
import { DataCardsCarousel } from '../BloodSugarResponsePage/DataCardsCarousel';
import { useParams } from 'react-router-dom';
import {
  useGetNutritionSummaryDetailV2
} from '@viome/viome-base/src/usecase/Home/HomeQuery';
import { FocusDescription } from '@viome/viome-base/src/usecase/Home/Home.model';
import BoostersBlockersDetailsShimmer from '@/components/skeleton/BoostersBlockersDetails';
import { ErrorLoadingPage } from '@screens/ErrorHandling/ErrorLoadingPage';
import { Render } from '@/components/Helpers/ComponentRender/Render';
import { EventTracker } from '@/utils';

export const MoodMemoryFocusPage = () => {
  const { id } = useParams();

  const {
    data: summaryDetailData,
    isLoading: nutritionDetailsLoading,
    isError: nutritionDetailsError,
  } = useGetNutritionSummaryDetailV2(Number(id));
  const [showAllL4, setShowAllL4] = useState(false);

  const nutritionSummaryDescriptions: FocusDescription[] =
    summaryDetailData?.my_focus || [];

  const displayedL4Cards = showAllL4
    ? nutritionSummaryDescriptions
    : nutritionSummaryDescriptions.slice(0, 4);
  const displayedBoosters = summaryDetailData?.booster;
  const displayedBlockers = summaryDetailData?.blocker;

  return nutritionDetailsLoading ? (
    <BoostersBlockersDetailsShimmer data-testid="boostersBlockers-shimmer" />
  ) : nutritionDetailsError ? (
    <ErrorLoadingPage data-testid="boostersBlockers-errorPage" />
  ) : (
    <section className="page-container grid grid-cols-1 !py-12" data-testid="boostersBlockers-page">
      <div className="min-h-screen bg-background-secondary space-y-8" data-testid="boostersBlockers-container">
        <ReturnButton data-testid="boostersBlockers-returnButton" />
        <div className="flex items-start justify-between flex-col gap-4" data-testid="boostersBlockers-header">
          <h1 className="typography-d2 text-primary" data-testid="boostersBlockers-title">
            {summaryDetailData?.title}
          </h1>
        </div>

        {(displayedL4Cards?.length > 0) && (
          <>
            <div className="typography-body text-secondary" data-testid="boostersBlockers-description">
              See how your health insights shape your personalized
              recommendations.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="boostersBlockers-l4CardsGrid">
              {displayedL4Cards?.map((card, index) => (
                <L4Card
                  key={card.title}
                  title={card.title}
                  description={card.description}
                  data-testid={`boostersBlockers-l4Card-${index}`}
                />
              ))}
            </div>
          </>
        )}

        {nutritionSummaryDescriptions &&
          nutritionSummaryDescriptions?.length > 4 && (
            <button
              onClick={() => {
                EventTracker.trackEvent('home', `boosterBlockers_allGoals`);
                setShowAllL4(!showAllL4);
              }}
              className={cn(
                'w-full py-4 rounded-xl',
                'bg-white',
                'flex items-center justify-center gap-2',
                'typography-body text-primary',
              )}
              data-testid="boostersBlockers-showAllButton"
            >
              {showAllL4 ? 'Show less' : 'Show all'}
              <ChevronDown
                className={cn(
                  'w-5 h-5 transition-transform',
                  showAllL4 && 'rotate-180',
                )}
                data-testid="boostersBlockers-showAllChevron"
              />
            </button>
          )}
        <Render
          when={
            summaryDetailData?.booster && summaryDetailData?.booster.length > 0
          }
        >
          <div className="space-y-6" data-testid="boostersBlockers-boostersSection">
            <div className="space-y-2" data-testid="boostersBlockers-boostersHeader">
              <h2 className="typography-h2 text-primary" data-testid="boostersBlockers-boostersTitle">My Boosters</h2>
              <p className="typography-body text-secondary" data-testid="boostersBlockers-boostersDescription">
                Explore your "boosters", the ones that drive improvement in your
                health journey.
              </p>
            </div>

            {displayedBoosters && (
              <div className="space-y-6" data-testid="boostersBlockers-boostersContent">
                <BadgeSection badgeItems={displayedBoosters} influencer='booster' data-testid="boostersBlockers-boostersBadges" />
              </div>
            )}
          </div>
        </Render>

        <Render
          when={
            summaryDetailData?.blocker && summaryDetailData?.blocker.length > 0
          }
        >
          <div className="space-y-6" data-testid="boostersBlockers-blockersSection">
            <div className="space-y-2" data-testid="boostersBlockers-blockersHeader">
              <h2 className="typography-h2 text-primary" data-testid="boostersBlockers-blockersTitle">My Blockers</h2>
              <p className="typography-body text-secondary" data-testid="boostersBlockers-blockersDescription">
                Meet your "blockers", the ones that may hinder progress in your
                health journey.
              </p>
            </div>

            {displayedBlockers && (
              <div className="space-y-6" data-testid="boostersBlockers-blockersContent">
                <BadgeSection badgeItems={displayedBlockers} influencer='blocker' data-testid="boostersBlockers-blockersBadges" />
              </div>
            )}
          </div>
        </Render>

        <DataCardsCarousel scoreId={Number(id)} data-testid="boostersBlockers-dataCarousel" />
      </div>
    </section>
  );
};
